import { useTranslation } from 'react-i18next';
import { Star, ShoppingBag, Truck, User } from 'lucide-react';

interface TestimonialProps {
  name: string;
  role: 'buyer' | 'seller' | 'delivery';
  rating: number;
  text: string;
  image: string;
}

const Testimonial = ({ name, role, rating, text, image }: TestimonialProps) => {
  const { t } = useTranslation();
  
  const getRoleIcon = () => {
    switch (role) {
      case 'buyer':
        return <ShoppingBag className="h-5 w-5 text-primary" />;
      case 'seller':
        return <User className="h-5 w-5 text-primary" />;
      case 'delivery':
        return <Truck className="h-5 w-5 text-primary" />;
    }
  };
  
  const getRoleLabel = () => {
    switch (role) {
      case 'buyer':
        return t('home.testimonials.buyer');
      case 'seller':
        return t('home.testimonials.seller');
      case 'delivery':
        return t('home.testimonials.delivery');
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col h-full">
      <div className="flex items-center mb-4">
        <img 
          src={image} 
          alt={name} 
          className="h-12 w-12 rounded-full object-cover mr-4"
        />
        <div>
          <h3 className="text-lg font-medium text-gray-900">{name}</h3>
          <div className="flex items-center">
            {getRoleIcon()}
            <span className="ml-1 text-sm text-gray-600">{getRoleLabel()}</span>
          </div>
        </div>
      </div>
      
      <div className="flex mb-3">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
          />
        ))}
      </div>
      
      <p className="text-gray-600 italic flex-grow">{text}</p>
    </div>
  );
};

export function TestimonialsSection() {
  const { t } = useTranslation();
  
  const testimonials: TestimonialProps[] = [
    {
      name: "Marie Dupont",
      role: "buyer",
      rating: 5,
      text: "J'ai trouvé exactement ce que je cherchais sur AEM Marketplace. La livraison a été rapide et le vendeur très professionnel. Je recommande vivement cette plateforme !",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
    },
    {
      name: "Jean Michel",
      role: "seller",
      rating: 5,
      text: "Depuis que j'ai rejoint AEM Marketplace, mes ventes ont augmenté de 200%. L'interface est intuitive et le support client est exceptionnel. Un vrai partenaire pour développer mon business !",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
    },
    {
      name: "Pierre Louis",
      role: "delivery",
      rating: 4,
      text: "Travailler comme livreur pour AEM me permet de gérer mon emploi du temps comme je le souhaite. L'application est facile à utiliser et les paiements sont toujours ponctuels.",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
    },
    {
      name: "Sophie Martin",
      role: "buyer",
      rating: 5,
      text: "J'adore la diversité des produits disponibles sur AEM. Les vendeurs sont réactifs et les prix sont compétitifs. C'est devenu mon site de shopping préféré !",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
    },
    {
      name: "Marc Antoine",
      role: "seller",
      rating: 4,
      text: "La plateforme AEM m'a permis de toucher une clientèle que je n'aurais jamais pu atteindre autrement. Les outils de gestion des commandes sont vraiment pratiques.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
    },
    {
      name: "Lucie Dubois",
      role: "delivery",
      rating: 5,
      text: "Les compétitions entre livreurs rendent le travail plus motivant ! J'apprécie vraiment l'esprit d'équipe et les bonus que nous pouvons gagner.",
      image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
    }
  ];
  
  return (
    <div className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            {t('home.testimonials.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-500">
            {t('home.testimonials.subtitle')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((testimonial, index) => (
            <Testimonial key={index} {...testimonial} />
          ))}
        </div>
      </div>
    </div>
  );
}